/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';
import type {
  ContentStackFeatureCarousel,
  FormattedContentStackHomepage,
} from '@/features/contentstack/interface';
import { CMSBanner } from '@/features/contentstack/components/CMSBanner/CMSBanner';
import { CMSButton } from '@/features/contentstack/components/CMSButton/CMSButton';
import { CMSCertona } from '@/features/contentstack/components/CMSCertona/CMSCertona';
import { CMSCertonaTabbed } from '@/features/contentstack/components/CMSCertonaTabbed/CMSCertonaTabbed';
import { CMSGrid } from '@/features/contentstack/components/CMSGrid/CMSGrid';
import { CMSBannerSplit } from '@/features/contentstack/components/CMSBannerSplit/CMSBannerSplit';
import { CMSFeaturedSpotlight } from '@/features/contentstack/components/CMSFeaturedSpotlight/CMSFeaturedSpotlight';
import { CMSGridBanner } from '@/features/contentstack/components/CMSGridBanner/CMSGridBanner';
import { CMSCitrus } from '@/features/contentstack/components/CMSCitrus/CMSCitrus';
import { CMSCitrusCarousel } from '@/features/contentstack/components/CMSCitrusCarousel/CMSCitrusCarousel';
import { CMSTarget } from '@/features/contentstack/components/CMSTarget/CMSTarget';
import { CMSExperienceInjector } from '@/features/contentstack/components/CMSExperienceInjector/CMSExperienceInjector';
import type { CertonaBlockProps, CertonaRecommendation } from '@/features/certona';
import { CMSPreFooter } from '@/features/contentstack/components/CMSPreFooter/CMSPreFooter';
import React from 'react';
import { useMemo } from 'react';
import { EmailSignup } from '@/components/ContentBlocks/EmailSignup';
import { CMSHomeSpacer } from '../CMSHomeSpacer';
import styles from './CMSModularHandler.module.scss';
import { STARCTypography } from '@/components/STARCTypography';
import { useCitrusHomepageData } from '@/features/citrus/api/getCitrusHomepageData';
import { SuperDuperHero } from '../SuperDuperHero/SuperDuperHero';
import { OptionalSuspense } from '@/features/performance/components/OptionalSuspense';
import { CMSYmme } from '@/features/contentstack/components/CMSYmme/CMSYmme';
import { pageTypes } from '@/constants/page';
import { CMSBlogGrid } from '@/features/contentstack/components/CMSBlogGrid/CMSBlogGrid';
import { usePageSpecificData } from '@/utils/analytics/usePageSpecificData';
import { ReviewCandidatesWithFallback } from '@/components/ActionCandidatesWithFallback/ReviewCandidatesWithFallback/ReviewCandidatesWithFallback';
import { BuyAgainCandidatesWithFallback } from '@/components/ActionCandidatesWithFallback/BuyAgainCandidatesWithFallback/BuyAgainCandidatesWithFallback';
import { useScrollIntoViewDOM } from '@/hooks/useScrollIntoView';
import dynamic from 'next/dynamic';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { useFeatureFlag } from '@/features/globalConfig';
import { CMSDealsSplit } from '@/features/contentstack/components/CMSDealsSplit/CMSDealsSplit';
import { getCMSModularBlockId } from '@/features/contentstack/utils/getCMSModularBlockId';
import { CMSPencilBanner } from '@/features/contentstack/components/CMSPencilBanner/CMSPencilBanner';
import LiveTextHero from '@/features/contentstack/components/CMSLiveTextHero/LiveTextHero';
import FeatureCarousel from '@/features/contentstack/components/CMSFeatureCarousel/FeatureCarousel';

const LazyHomepageHero = dynamic(
  () => import('../HomepageHero/HomepageHero').then((mod) => mod.HomepageHero),
  { ssr: true },
);

type Props = {
  certonaRecommendations: CertonaRecommendation[];
  content: FormattedContentStackHomepage['homepageBody'];
  contentSeo: FormattedContentStackHomepage['seoTags'];
  setCertonaRecommendationAddedToCart?: React.Dispatch<React.SetStateAction<boolean>>;
} & CertonaBlockProps;

type FeatureCarouselModularBlock = {
  feature_carousel: {
    feature_carousel: ContentStackFeatureCarousel[];
  };
};

const isFeatureCarouselModularBlock = (
  modularBlock: object,
): modularBlock is FeatureCarouselModularBlock => {
  if (!('feature_carousel' in modularBlock)) {
    return false;
  }

  const featureCarousel = modularBlock.feature_carousel;

  return (
    typeof featureCarousel === 'object' &&
    featureCarousel !== null &&
    'feature_carousel' in featureCarousel &&
    Array.isArray(featureCarousel.feature_carousel)
  );
};

function CMSCitrusSpacer({ children }: { children: React.ReactNode }) {
  return <CMSHomeSpacer shouldIgnoreCarouselSpacing>{children}</CMSHomeSpacer>;
}

export const CMSModularHandler = ({
  content,
  contentSeo,
  certonaRecommendations,
  setCertonaRecommendationAddedToCart,
  ...certonaProps
}: Props) => {
  const dateRange = Number(useFeatureFlag('BUY_AGAIN_DATE_RANGE'));
  const heroIndices = useMemo(() => {
    let heroComponentIndex: number | null = null;
    let superDuperHeroIndex: number | null = null;

    content?.forEach((modularBlock, i) => {
      if (modularBlock.hero_component?.hero_component?.[0] && heroComponentIndex === null) {
        heroComponentIndex = i;
      }
      if (modularBlock.super_duper_hero?.super_duper_hero?.[0] && superDuperHeroIndex === null) {
        superDuperHeroIndex = i;
      }
    });

    return { heroComponentIndex, superDuperHeroIndex };
  }, [content]);
  const { data: citrusAds } = useCitrusHomepageData({
    enabled: content?.some((block) => !!block.citrus_ad?.citrus_ad?.[0]),
  });
  useScrollIntoViewDOM();
  const { setPageSpecificData } = usePageSpecificData();
  const router = useLegacyRouter();
  const { intcmp } = router.query;
  const intcmpValue = Array.isArray(intcmp) ? intcmp[0] : intcmp;
  const intcmpCode = typeof intcmpValue === 'string' ? intcmpValue.split(':')[0] : '';
  const pfmLocation =
    intcmpCode === 'HOM'
      ? 'Home Page'
      : intcmpCode === 'LAP'
        ? 'Landing Page'
        : intcmpCode === 'CAT'
          ? 'Category Page'
          : undefined;
  const trackAnalytics = () => {
    setPageSpecificData({
      pfmLocation: pfmLocation || '',
      productFindingMethod: intcmpCode ? 'Internal Campaign' : '',
      internalCampaignInteraction: intcmpCode ? 'internalCampaignInteraction' : '',
    });
  };
  useScrollIntoViewDOM();
  return (
    <STARCTypography>
      <span className="sr-only">
        <h1>{contentSeo?.page_heading || 'Auto Parts at AutoZone'}</h1>
      </span>
      {content?.map((modularBlock, i) => {
        const getBlockId = (blockName: string) => getCMSModularBlockId(modularBlock, blockName);
        const shouldReduceSpacing = i === 0;
        const customSpacingRequired =
          heroIndices.heroComponentIndex !== null &&
          (heroIndices.heroComponentIndex as number) + 1 === i;
        const customClass = customSpacingRequired ? styles.customSpacer : undefined;

        if (modularBlock.button) {
          return (
            <div id={getBlockId('button')} key={i}>
              <CMSButton options={modularBlock.button} />
            </div>
          );
        }
        if (modularBlock.ymme?.ymme?.[0]) {
          return (
            <OptionalSuspense key={i}>
              <CMSHomeSpacer className={customClass} id={getBlockId('ymme')}>
                <CMSYmme nonShopPage={true} removeMargins pageType={pageTypes.home} />
              </CMSHomeSpacer>
            </OptionalSuspense>
          );
        }

        if (modularBlock.banner?.banner?.[0]?.asset_size) {
          const heroIndex = heroIndices.heroComponentIndex ?? heroIndices.superDuperHeroIndex ?? -1;
          const isBeforeHero = heroIndex !== -1 && i < heroIndex;

          const loading = isBeforeHero ? 'eager' : undefined;

          return (
            <OptionalSuspense key={i}>
              <CMSHomeSpacer
                shouldReduceSpacing={shouldReduceSpacing}
                className={customClass}
                id={getBlockId('banner')}
              >
                <CMSBanner content={modularBlock.banner.banner[0]} loading={loading} />
              </CMSHomeSpacer>
            </OptionalSuspense>
          );
        }

        if (modularBlock.certona_tabbed?.certona_tabbed?.[0]) {
          return (
            <OptionalSuspense key={i}>
              <CMSHomeSpacer
                shouldIgnoreCarouselSpacing
                className={customClass}
                id={getBlockId('certona_tabbed')}
              >
                <CMSCertonaTabbed
                  content={modularBlock.certona_tabbed.certona_tabbed[0]}
                  certonaRecommendations={certonaRecommendations}
                  onCertonaAddToCartClick={() => {
                    setCertonaRecommendationAddedToCart?.(true);
                  }}
                  {...certonaProps}
                />
              </CMSHomeSpacer>
            </OptionalSuspense>
          );
        }

        if (modularBlock.certona?.certona?.[0]) {
          return (
            <OptionalSuspense key={i}>
              <CMSHomeSpacer
                shouldIgnoreCarouselSpacing
                className={customClass}
                id={getBlockId('certona')}
              >
                <CMSCertona
                  content={modularBlock.certona.certona[0]}
                  certonaRecommendations={certonaRecommendations}
                  onCertonaAddToCartClick={() => {
                    setCertonaRecommendationAddedToCart?.(true);
                  }}
                  {...certonaProps}
                />
              </CMSHomeSpacer>
            </OptionalSuspense>
          );
        }

        if (modularBlock.parttype_grid?.parttype_grid?.[0]) {
          return (
            <OptionalSuspense key={i}>
              <CMSHomeSpacer className={customClass} id={getBlockId('parttype_grid')}>
                <CMSGrid
                  content={modularBlock.parttype_grid.parttype_grid[0]}
                  headlineOptions={{ underline: true }}
                  onAnalyticsClick={trackAnalytics}
                />
              </CMSHomeSpacer>
            </OptionalSuspense>
          );
        }

        if (modularBlock.banner_split?.banner_split?.[0]) {
          return (
            <OptionalSuspense key={i}>
              <CMSHomeSpacer className={customClass} id={getBlockId('banner_split')}>
                <CMSBannerSplit
                  content={modularBlock.banner_split.banner_split[0]}
                  headlineOptions={{ underline: true }}
                />
              </CMSHomeSpacer>
            </OptionalSuspense>
          );
        }

        if (modularBlock.featured_spotlight?.feature_spotlight?.[0]) {
          return (
            <OptionalSuspense key={i}>
              <CMSHomeSpacer className={customClass} id={getBlockId('featured_spotlight')}>
                <CMSFeaturedSpotlight
                  content={modularBlock.featured_spotlight.feature_spotlight[0]}
                />
              </CMSHomeSpacer>
            </OptionalSuspense>
          );
        }

        if (modularBlock.blogtype_grid?.blogtype_grid?.[0]) {
          return (
            <OptionalSuspense key={i}>
              <CMSHomeSpacer className={customClass} id={getBlockId('blogtype_grid')}>
                <CMSBlogGrid content={modularBlock.blogtype_grid.blogtype_grid[0]} />
              </CMSHomeSpacer>
            </OptionalSuspense>
          );
        }

        if (modularBlock.target_div?.target_div?.[0]) {
          return (
            <OptionalSuspense key={i}>
              <CMSHomeSpacer className={customClass} id={getBlockId('target_div')}>
                <CMSTarget content={modularBlock.target_div.target_div[0]} />
              </CMSHomeSpacer>
            </OptionalSuspense>
          );
        }
        if (modularBlock.deals_split?.deals_split?.[0]) {
          return (
            <OptionalSuspense key={i}>
              <CMSHomeSpacer key={i} id={getBlockId('deals_split')}>
                <CMSDealsSplit content={modularBlock.deals_split?.deals_split[0]} />
              </CMSHomeSpacer>
            </OptionalSuspense>
          );
        }

        if (modularBlock.experience_injector?.experience_injector?.[0]) {
          return (
            <OptionalSuspense key={i}>
              <CMSHomeSpacer className={customClass} id={getBlockId('experience_injector')}>
                <CMSExperienceInjector
                  content={modularBlock.experience_injector.experience_injector[0]}
                />
              </CMSHomeSpacer>
            </OptionalSuspense>
          );
        }

        if (modularBlock.grid_banner?.grid_banner?.[0]) {
          return (
            <OptionalSuspense key={i}>
              <CMSHomeSpacer className={customClass} id={getBlockId('grid_banner')}>
                <CMSGridBanner content={modularBlock.grid_banner.grid_banner[0]} />
              </CMSHomeSpacer>
            </OptionalSuspense>
          );
        }

        if (modularBlock.hero_component?.hero_component?.[0]) {
          return (
            <OptionalSuspense key={`${i}-hero`}>
              <CMSHomeSpacer removeHorizontalSpacing id={getBlockId('hero_component')}>
                <LazyHomepageHero options={modularBlock.hero_component.hero_component[0]} />
              </CMSHomeSpacer>
            </OptionalSuspense>
          );
        }

        if (modularBlock.super_duper_hero?.super_duper_hero?.[0]) {
          const cardData = content.find((block) => 'horizontal_scroll_cards' in block)
            ?.horizontal_scroll_cards?.horizontal_scroll_cards?.[0]?.card_content;

          return (
            <OptionalSuspense key={`${i}-hero`}>
              <CMSHomeSpacer
                removeHorizontalSpacing
                removeVerticalSpacing
                id={getBlockId('super_duper_hero')}
              >
                <SuperDuperHero
                  options={modularBlock.super_duper_hero.super_duper_hero[0]}
                  horizontalCards={cardData}
                />
              </CMSHomeSpacer>
            </OptionalSuspense>
          );
        }

        if (modularBlock.live_text_hero?.live_text_hero?.[0]) {
          return (
            <OptionalSuspense key={`${i}-live-text-hero`}>
              <CMSHomeSpacer
                removeHorizontalSpacing
                removeVerticalSpacing
                id={getBlockId('live_text_hero')}
              >
                <LiveTextHero options={modularBlock.live_text_hero.live_text_hero[0]} />
              </CMSHomeSpacer>
            </OptionalSuspense>
          );
        }

        if (
          isFeatureCarouselModularBlock(modularBlock) &&
          modularBlock.feature_carousel.feature_carousel[0]
        ) {
          return (
            <OptionalSuspense key={`${i}-feature-carousel`}>
              <CMSHomeSpacer id={getBlockId('feature_carousel')}>
                <FeatureCarousel options={modularBlock.feature_carousel.feature_carousel[0]} />
              </CMSHomeSpacer>
            </OptionalSuspense>
          );
        }

        if (modularBlock.prefooter) {
          return (
            <OptionalSuspense key={i}>
              <CMSHomeSpacer className={customClass} id={getBlockId('prefooter')}>
                <CMSPreFooter content={modularBlock.prefooter.prefooter?.[0]?.column} />
              </CMSHomeSpacer>
            </OptionalSuspense>
          );
        }

        if (modularBlock.email_block) {
          return (
            <OptionalSuspense key={i}>
              <div id={getBlockId('email_block')}>
                <EmailSignup />
              </div>
            </OptionalSuspense>
          );
        }

        if (modularBlock.citrus_ad?.citrus_ad?.[0]) {
          return (
            <OptionalSuspense key={i}>
              {modularBlock.citrus_ad.citrus_ad[0].type === 'carousel' ? (
                <CMSHomeSpacer shouldIgnoreCarouselSpacing id={getBlockId('citrus_ad')}>
                  <CMSCitrusCarousel />
                </CMSHomeSpacer>
              ) : (
                <div id={getBlockId('citrus_ad')}>
                  <CMSCitrus
                    content={modularBlock.citrus_ad.citrus_ad[0]}
                    citrusAds={citrusAds?.ads}
                    SpacerComponent={CMSCitrusSpacer}
                  />
                </div>
              )}
            </OptionalSuspense>
          );
        }
        if (modularBlock.reviews_container?.reviews_container?.[0]) {
          const reviewCandidatesContainer = modularBlock.reviews_container?.reviews_container?.[0];
          return (
            <div id={getBlockId('reviews_container')} key={i}>
              <ReviewCandidatesWithFallback
                shouldReduceSpacing={shouldReduceSpacing}
                customClass={customClass}
                limit={reviewCandidatesContainer?.product_review_limit ?? 0}
                title={reviewCandidatesContainer?.review_title ?? ''}
                fallbackBanner={reviewCandidatesContainer?.fallback_banner?.[0]}
              />
            </div>
          );
        }
        if (modularBlock.buy_again_container?.buy_again_container?.[0]) {
          const buyAgainCandidatesContainer =
            modularBlock.buy_again_container?.buy_again_container?.[0];
          return (
            <div id={getBlockId('buy_again_container')} key={i}>
              <BuyAgainCandidatesWithFallback
                shouldReduceSpacing={shouldReduceSpacing}
                customClass={customClass}
                limit={buyAgainCandidatesContainer?.product_buy_again_limit ?? 0}
                title={buyAgainCandidatesContainer?.buy_again_title ?? ''}
                dateRange={dateRange}
                fallbackBanner={buyAgainCandidatesContainer?.fallback_banner?.[0]}
                SpacerComponent={CMSHomeSpacer}
                sessionStorageKey="showHomeBuyAgainCandidates"
              />
            </div>
          );
        }

        if ('pencil_banner' in modularBlock && modularBlock.pencil_banner?.pencil_banner?.[0]) {
          return (
            <OptionalSuspense key={i}>
              <CMSHomeSpacer
                className={customClass}
                {...(modularBlock.pencil_banner.pencil_banner[0].component_id && {
                  id: modularBlock.pencil_banner.pencil_banner[0].component_id,
                })}
              >
                <CMSPencilBanner content={modularBlock.pencil_banner.pencil_banner[0]} />
              </CMSHomeSpacer>
            </OptionalSuspense>
          );
        }

        return null;
      })}
    </STARCTypography>
  );
};
